import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErrorMessage, formatCurrency, formatDate, formatDateShort } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import ReceivePaymentDialog from "@/components/ReceivePaymentDialog";
import { buildWhatsAppUrl, paymentReminderMessage } from "@/lib/whatsapp";
import { WhatsappLogo, Coins, Receipt, ClockCounterClockwise, ArrowRight } from "@phosphor-icons/react";

const MODE_LABELS = {
    cash: "Cash", upi: "UPI", bank_transfer: "Bank", cheque: "Cheque", other: "Other",
};

export default function CustomerDetailDrawer({ customerId, open, onOpenChange, onUpdate }) {
    const [summary, setSummary] = useState(null);
    const [ledger, setLedger] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paymentOpen, setPaymentOpen] = useState(false);
    const navigate = useNavigate();

    const load = async () => {
        if (!customerId) return;
        setLoading(true);
        try {
            const [s, l] = await Promise.all([
                api.get(`/customers/${customerId}/summary`),
                api.get(`/customers/${customerId}/ledger`),
            ]);
            setSummary(s.data);
            setLedger(l.data.entries || []);
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, customerId]);

    const customer = summary?.customer;

    const sendWhatsAppReminder = () => {
        if (!customer) return;
        const msg = paymentReminderMessage({
            customerName: customer.name,
            pendingAmount: summary.total_pending,
            invoiceNo: summary.last_invoice || "",
        });
        window.open(buildWhatsAppUrl(customer.whatsapp || customer.mobile, msg), "_blank");
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="customer-drawer">
                {!summary || loading ? (
                    <div className="p-6 text-sm text-neutral-500">Loading customer...</div>
                ) : (
                    <>
                        <SheetHeader className="pb-4 border-b border-neutral-200">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-bold font-display shrink-0"
                                     style={{ backgroundColor: "#15803D" }}>
                                    {customer.name?.[0]?.toUpperCase() || "?"}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <SheetTitle className="text-2xl font-extrabold font-display tracking-tight text-left">{customer.name}</SheetTitle>
                                    <div className="text-xs text-neutral-500 mt-1 font-mono uppercase tracking-wider">ID: {customer.id.slice(0, 8)}</div>
                                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-neutral-600">
                                        {customer.mobile && <span>📱 {customer.mobile}</span>}
                                        {customer.email && <span>✉ {customer.email}</span>}
                                        {customer.gst_number && <span className="font-mono">GST: {customer.gst_number}</span>}
                                    </div>
                                    {customer.address && <div className="text-xs text-neutral-500 mt-1">{customer.address}</div>}
                                </div>
                            </div>
                        </SheetHeader>

                        {/* Summary cards */}
                        <div className="grid grid-cols-2 gap-3 mt-4">
                            <SummaryCard label="Total Purchase" value={formatCurrency(summary.total_purchase)} tone="neutral" testId="drawer-total-purchase" />
                            <SummaryCard label="Total Paid" value={formatCurrency(summary.total_paid)} tone="green" testId="drawer-total-paid" />
                            <SummaryCard label="Pending" value={formatCurrency(summary.total_pending)} tone={summary.total_pending > 0 ? "red" : "green"} testId="drawer-pending" />
                            <SummaryCard label="Overdue (30+ days)" value={formatCurrency(summary.overdue_amount)} tone={summary.overdue_amount > 0 ? "red" : "neutral"} testId="drawer-overdue" />
                            <SummaryCard label="Credit Limit" value={formatCurrency(summary.credit_limit)} tone="neutral" testId="drawer-credit-limit" />
                            <SummaryCard label="Credit Available" value={formatCurrency(summary.credit_available)} tone="green" testId="drawer-credit-available" />
                        </div>

                        <div className="grid grid-cols-2 gap-3 mt-4">
                            <div className="bg-neutral-50 rounded-md p-3 text-xs">
                                <div className="uppercase tracking-widest text-[10px] font-bold text-neutral-500">Last Invoice</div>
                                <div className="font-mono font-semibold mt-1 text-neutral-900">{summary.last_invoice || "—"}</div>
                            </div>
                            <div className="bg-neutral-50 rounded-md p-3 text-xs">
                                <div className="uppercase tracking-widest text-[10px] font-bold text-neutral-500">Last Payment</div>
                                <div className="font-semibold mt-1 text-neutral-900">{summary.last_payment_date ? formatDateShort(summary.last_payment_date) : "—"}</div>
                            </div>
                        </div>

                        {/* Action buttons */}
                        <div className="flex flex-wrap gap-2 mt-4">
                            <Button
                                onClick={() => setPaymentOpen(true)}
                                className="text-white flex-1 min-w-[160px]"
                                style={{ backgroundColor: "#15803D" }}
                                data-testid="drawer-receive-payment-btn"
                            >
                                <Coins size={16} className="mr-1.5" /> Receive Payment
                            </Button>
                            <Button
                                variant="outline"
                                onClick={sendWhatsAppReminder}
                                disabled={!summary.total_pending}
                                className="flex-1 min-w-[160px]"
                                data-testid="drawer-whatsapp-btn"
                            >
                                <WhatsappLogo size={16} className="mr-1.5" style={{ color: "#25D366" }} weight="fill" /> WhatsApp Reminder
                            </Button>
                        </div>

                        {/* Ledger */}
                        <div className="mt-6">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-lg font-bold font-display flex items-center gap-2">
                                    <ClockCounterClockwise size={18} weight="duotone" /> Ledger
                                </h3>
                                <span className="text-xs text-neutral-500">Closing balance: <b className="text-neutral-900">{formatCurrency(ledger.length ? ledger[ledger.length - 1].balance : 0)}</b></span>
                            </div>
                            <div className="border border-neutral-200 rounded-md overflow-hidden" data-testid="drawer-ledger">
                                <div className="overflow-x-auto max-h-[340px]">
                                    <table className="w-full text-xs">
                                        <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500 sticky top-0">
                                            <tr>
                                                <th className="text-left px-3 py-2 font-semibold">Date</th>
                                                <th className="text-left px-3 py-2 font-semibold">Type</th>
                                                <th className="text-left px-3 py-2 font-semibold">Reference</th>
                                                <th className="text-right px-3 py-2 font-semibold">Debit</th>
                                                <th className="text-right px-3 py-2 font-semibold">Credit</th>
                                                <th className="text-right px-3 py-2 font-semibold">Balance</th>
                                                <th className="px-3 py-2"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ledger.length === 0 && (
                                                <tr><td colSpan={7} className="text-center py-6 text-neutral-400">No transactions yet</td></tr>
                                            )}
                                            {ledger.map((e, i) => (
                                                <tr key={i} className="border-t border-neutral-100">
                                                    <td className="px-3 py-2 whitespace-nowrap text-neutral-600">{formatDateShort(e.date)}</td>
                                                    <td className="px-3 py-2">
                                                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                                                              style={{ background: e.type === "invoice" ? "#FEE2E2" : "#DCFCE7", color: e.type === "invoice" ? "#DC2626" : "#15803D" }}>
                                                            {e.type}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 font-mono text-neutral-700">{e.reference}</td>
                                                    <td className="px-3 py-2 text-right tabular font-semibold">{e.debit > 0 ? formatCurrency(e.debit) : ""}</td>
                                                    <td className="px-3 py-2 text-right tabular font-semibold" style={{ color: "#15803D" }}>{e.credit > 0 ? formatCurrency(e.credit) : ""}</td>
                                                    <td className="px-3 py-2 text-right tabular font-bold">{formatCurrency(e.balance)}</td>
                                                    <td className="px-3 py-2 text-right">
                                                        {e.type === "invoice" && e.sale_id && (
                                                            <button onClick={() => navigate(`/invoice/${e.sale_id}`)} className="text-neutral-500 hover:text-neutral-900" title="Open invoice">
                                                                <ArrowRight size={12} />
                                                            </button>
                                                        )}
                                                        {e.type === "payment" && e.payment_id && (
                                                            <button onClick={() => navigate(`/receipt/${e.payment_id}`)} className="text-neutral-500 hover:text-neutral-900" title="Open receipt">
                                                                <Receipt size={12} />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <ReceivePaymentDialog
                            open={paymentOpen}
                            onOpenChange={setPaymentOpen}
                            defaultCustomerId={customer.id}
                            onSaved={() => { load(); onUpdate && onUpdate(); }}
                        />
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
}

function SummaryCard({ label, value, tone = "neutral", testId }) {
    const bg = { neutral: "#F5F5F5", green: "#DCFCE7", red: "#FEE2E2" }[tone];
    const fg = { neutral: "#0A0A0A", green: "#15803D", red: "#DC2626" }[tone];
    return (
        <div className="rounded-md p-3" style={{ background: bg }} data-testid={testId}>
            <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: fg, opacity: 0.7 }}>{label}</div>
            <div className="text-lg font-extrabold font-display tabular mt-1" style={{ color: fg }}>{value}</div>
        </div>
    );
}
